import React, { useMemo, useState } from 'react';
import { fmtCurrency, fmtDuration, fmtNumber, fmtPercent } from './shared/format';
import InfoTip from '../shared/InfoTip';
import { KpiCard } from '../shared/KpiCard';
import SelectMenu from '../shared/SelectMenu';

const COMPARISON_OPTIONS = [
  { value: 'previous_period', label: 'Previous Period' },
  { value: 'previous_year', label: 'Previous Year' },
  { value: 'off', label: 'None' },
];

/* ----------------------------- KpiCards ----------------------------- */
const KpiCards = ({ kpis, loading, comparisonMode, setComparisonMode, getComparisonLabel, trendData }) => {
  const [showSecondary, setShowSecondary] = useState(false);

  const comp = kpis?.comparison ?? null;

  // Build sparkline data from daily trend
  const sparkSessions = useMemo(() => (trendData || []).map(day => ({ v: Number(day.sessions) || 0 })), [trendData]);
  const sparkUsers = useMemo(() => (trendData || []).map(day => ({ v: Number(day.users) || 0 })), [trendData]);
  const sparkConversions = useMemo(() => (trendData || []).map(day => ({ v: Number(day.conversions) || 0 })), [trendData]);
  const showRevenue = loading || (kpis?.total_revenue ?? 0) > 0 || (kpis?.purchase_event_count ?? 0) > 0;
  const conversionRate = kpis && kpis.sessions > 0 ? (kpis.conversions / kpis.sessions) * 100 : 0;
  // Conversion rate is conversions/sessions, so its change is a ratio of ratios — not the
  // difference of the two percent changes. Guard against a -100% sessions change (divide-by-zero).
  const convRateChangePct = (() => {
    if (!comp || comp.conversions_change_pct == null || comp.sessions_change_pct == null) return null;
    const sessionsFactor = 1 + comp.sessions_change_pct / 100;
    if (sessionsFactor <= 0) return null;
    const convFactor = 1 + comp.conversions_change_pct / 100;
    return (convFactor / sessionsFactor - 1) * 100;
  })();

  // Check if comparison was requested but all change values came back null (no data for that period)
  const comparisonEmpty = comparisonMode !== "off" && comp != null &&
    [comp.sessions_change_pct, comp.users_change_pct, comp.conversions_change_pct,
     comp.bounce_rate_change_pct, comp.total_revenue_change_pct, comp.pageviews_change_pct].every(changePct => changePct === null || changePct === undefined);
  const comparisonNoData = comparisonMode !== "off" && !loading && kpis && (!comp || comparisonEmpty);

  return (
    <>
      {/* ============ KPI CARDS ============ */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            {getComparisonLabel() && (
              <span className="text-[13px] text-gray-400 dark:text-gray-500">
                {getComparisonLabel()}
                {comparisonNoData && (
                  <span className="ml-2 text-amber-500 dark:text-amber-400">
                    — No data available for the comparison period
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 max-[344px]:w-full">
            <InfoTip text={<>Shows the % change on each KPI card by comparing the current date range against the selected period.<br /><br />When comparison is enabled, the Sessions Trend chart also overlays the previous period.</>} />
            <span className="text-xs text-gray-500 dark:text-gray-400">Compare:</span>
            <SelectMenu
              options={COMPARISON_OPTIONS}
              value={comparisonMode}
              onChange={setComparisonMode}
              ariaLabel="Comparison period"
              className="text-xs max-[344px]:ml-auto"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <KpiCard
            title="SESSIONS"
            value={kpis ? fmtNumber(kpis.sessions) : '0'}
            subtitle="Total website visits"
            changePct={comp?.sessions_change_pct ?? null}
            direction="higher_good"
            variant="primary"
            icon="chart"
            loading={loading}
            tooltip="Total visits to the site over the selected range. One person can have multiple sessions if they return after a break or across days."
            sparkData={sparkSessions}
          />
          <KpiCard
            title="USERS"
            value={kpis ? fmtNumber(kpis.users) : '0'}
            subtitle="Unique visitors"
            changePct={comp?.users_change_pct ?? null}
            direction="higher_good"
            variant="primary"
            icon="users"
            loading={loading}
            tooltip="Unique visitors during the range. Someone who visits multiple times — even across days — still counts as one user."
            sparkData={sparkUsers}
          />
          <KpiCard
            title="BOUNCE RATE"
            value={kpis ? fmtPercent(kpis.bounce_rate) : '0.0%'}
            subtitle="Single-page visits"
            changePct={comp?.bounce_rate_change_pct ?? null}
            direction="lower_good"
            variant="muted"
            icon="bounce"
            loading={loading}
            tooltip="Share of sessions that left without engagement — no second pageview, conversion, or 10+ seconds of time. Lower is better."
            valueTooltip="Bounce rate for the whole range, weighted toward busier days so a quiet day with unusual traffic doesn't swing the number. (How: each day's bounce rate averaged across the range, weighted by that day's session count.)"
            sparkData={sparkSessions}
          />
          <KpiCard
            title="CONVERSIONS"
            value={kpis ? fmtNumber(kpis.conversions) : '0'}
            subtitle="Completed bookings"
            changePct={comp?.conversions_change_pct ?? null}
            direction="higher_good"
            variant="success"
            icon="conversions"
            loading={loading}
            tooltip="Count of conversion events fired during the range (purchases and other goals you've configured in GA4)."
            sparkData={sparkConversions}
          />
          {showRevenue && (
            <KpiCard
              title="REVENUE"
              value={kpis ? fmtCurrency(kpis.total_revenue) : '$0.00'}
              subtitle="Booking revenue"
              changePct={comp?.total_revenue_change_pct ?? null}
              direction="higher_good"
              variant="success"
              icon="dollar"
              loading={loading}
              tooltip="Total dollar value of purchase events fired during the range. Other conversion goals like form submits or signups don't count toward this number."
              sparkData={sparkConversions}
            />
          )}
          <KpiCard
            title="CONVERSION RATE"
            value={kpis ? `${conversionRate.toFixed(2)}%` : '0.00%'}
            subtitle="Sessions → bookings"
            changePct={convRateChangePct}
            direction="higher_good"
            variant="subtle"
            icon="percent"
            loading={loading}
            tooltip="Share of sessions that converted. Sensitive to traffic mix — a campaign sending unqualified traffic can drag this down even when bookings rise."
            valueTooltip="Share of all sessions in the range that turned into a conversion. Uses period totals, not an average of daily rates. (How: total conversions divided by total sessions, then multiplied by 100.)"
            sparkData={sparkConversions}
          />
        </div>
      </div>

      {/* ============ SUPPORTING METRICS (expandable) ============ */}
      <div>
        <button
          type="button"
          onClick={() => setShowSecondary((isShown) => !isShown)}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold text-[#32B1FF] hover:text-[#1f9fee] bg-[#32B1FF]/10 hover:bg-[#32B1FF]/15 border border-[#32B1FF]/20 rounded-full px-3 py-1.5 transition ${showSecondary ? 'mb-3' : ''}`}
        >
          {showSecondary ? 'Hide' : 'Show'} supporting metrics
          <svg className={`w-3.5 h-3.5 transition-transform ${showSecondary ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showSecondary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-5 gap-3 sm:gap-4">
            <KpiCard title="NEW USERS" value={kpis ? fmtNumber(kpis.new_users) : '0'} subtitle="First-time visitors"
              changePct={comp?.new_users_change_pct ?? null} direction="higher_good" variant="muted" icon="users" loading={loading}
              tooltip="People visiting the site for the first time during the range. GA4 recognizes returning visitors via a cookie, so a private-browsing or new-device visit counts as new."
              sparkData={sparkUsers} />
            <KpiCard title="PAGEVIEWS" value={kpis ? fmtNumber(kpis.pageviews) : '0'} subtitle="Total pages viewed"
              changePct={comp?.pageviews_change_pct ?? null} direction="higher_good" variant="muted" icon="chart" loading={loading}
              tooltip="Total page loads. A single session typically has 2–5 pageviews."
              sparkData={sparkSessions} />
            <KpiCard title="AVG. SESSION DURATION" value={kpis ? fmtDuration(kpis.avg_session_duration) : '0:00'} subtitle="Time on site per visit"
              changePct={comp?.avg_session_duration_change_pct ?? null} direction="higher_good" variant="subtle" icon="chart" loading={loading}
              tooltip="Average time spent on the site per session. Inflated for sessions where the visitor leaves the tab open in the background."
              sparkData={sparkSessions} />
            <KpiCard title="ENGAGEMENT RATE" value={kpis ? fmtPercent(kpis.engagement_rate) : '0.0%'} subtitle="Engaged sessions"
              changePct={comp?.engagement_rate_change_pct ?? null} direction="higher_good" variant="subtle" icon="chart" loading={loading}
              tooltip={"Share of sessions GA4 marks as engaged — 10+ seconds, a conversion, or a second pageview. Roughly the inverse of bounce rate."}
              sparkData={sparkSessions} />
            <KpiCard title="PAGES / SESSION" value={kpis ? Number(kpis.pages_per_session || 0).toFixed(1) : '0.0'} subtitle="Avg pages per visit"
              changePct={comp?.pages_per_session_change_pct ?? null} direction="higher_good" variant="muted" icon="chart" loading={loading}
              tooltip="Average distinct pages viewed per session. Higher = more thorough exploration."
              sparkData={sparkSessions} />
          </div>
        )}
      </div>
    </>
  );
};

export default KpiCards;
